import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Heading,
  Text,
  Card,
  CardBody,
  Icon
} from '@chakra-ui/react';
import { ArtistModel, ItemModel } from 'models';
import { ArtistGrid, ItemGrid, Wizard } from 'components';
import { ItemGridSelectionType } from 'components/Item/ItemGrid/ItemGrid';
import { ArtistGridSelectionType } from 'components/Artist/ArtistGrid/ArtistGrid';
import { useArtists } from 'hooks/queries/useArtists';
import { useArtistItems } from 'hooks/queries/useArtists';
import { AiFillCheckCircle } from 'react-icons/ai';

export interface MergeCreationWizardProps {
  defaultArtistId?: string;
  onSubmit: (selectedItems: ItemModel[]) => void;
  isLoading?: boolean;
}

export default function MergeCreationWizard({
  defaultArtistId,
  onSubmit,
  isLoading = false
}: MergeCreationWizardProps) {
  const [selectedArtist, setSelectedArtist] = useState<ArtistModel | null>(null);
  const [selectedItems, setSelectedItems] = useState<ItemModel[]>([]);

  const { data: artists = [], isLoading: artistsLoading } = useArtists();
  const activeArtistId = useMemo(
    () => selectedArtist?.id || defaultArtistId || '',
    [selectedArtist, defaultArtistId]
  );
  const {
    data: artistItems = [],
    isLoading: artistItemsLoading,
    isFetching: artistItemsFetching
  } = useArtistItems(activeArtistId);

  useEffect(() => {
    if (defaultArtistId && artists.length > 0) {
      const artist = artists.find(a => a.id === defaultArtistId);
      if (artist) {
        setSelectedArtist(artist);
      }
    }
  }, [defaultArtistId, artists]);

  useEffect(() => {
    setSelectedItems([]);
  }, [activeArtistId]);

  const handleWizardSubmit = () => {
    if (selectedItems.length >= 2) {
      onSubmit(selectedItems);
    }
  };

  const renderArtistSelection = () => (
    <Stack spacing={4}>
      <ArtistGrid
        artists={artists}
        isLoading={artistsLoading}
        showFilters={true}
        showSort={false}
        gridDisplay={true}
        gridSelection={ArtistGridSelectionType.SINGLE}
        defaultSelectedArtists={selectedArtist ? [selectedArtist] : []}
        onArtistCardClick={setSelectedArtist}
      />
    </Stack>
  );

  const renderItemSelection = () => (
    <Stack spacing={4}>
      <Box>
        <ItemGrid
          items={artistItems}
          isLoading={artistItemsLoading || artistItemsFetching || isLoading}
          showFilters={true}
          gridDisplay={true}
          gridMaxHeight={400}
          gridSelection={ItemGridSelectionType.MULTI}
          defaultSelectedItems={selectedItems}
          onItemCardClick={(item) => {
            setSelectedItems(prev => {
              const exists = prev.find(i => i.id === item.id);
              if (exists) {
                return prev.filter(i => i.id !== item.id);
              }
              return [...prev, item];
            });
          }}
          paginate={false}
          showMetadata={true}
          defaultMetadataExpanded={true}
        />
      </Box>
    </Stack>
  );

  const renderConfirmation = () => (
    <Stack spacing={4}>
        <Box>
          <ItemGrid
            items={selectedItems}
            isLoading={isLoading}
            showFilters={false}
            gridDisplay={true}
            gridMaxHeight={400}
            paginate={false}
            showMetadata={true}
            defaultMetadataExpanded={true}
          />
        </Box>
    </Stack>
  );

  const renderFinishStep = () => (
    <Stack gap={4} alignItems="center">
      <Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
      <Heading size="sm">Merge Request Created Successfully</Heading>
      <Text fontSize="sm" color="gray.600">
        Your merge request has been submitted for review.
      </Text>
    </Stack>
  );

  const wizardSteps = defaultArtistId
    ? [
        {
          label: 'Select Items',
          description: 'Choose items to merge',
          content: renderItemSelection(),
          requiresValidation: true,
          isValidated: selectedItems.length >= 2
        },
        {
          label: 'Confirm',
          description: 'Review and submit',
          content: renderConfirmation()
        }
      ]
    : [
        {
          label: 'Select Artist',
          description: 'Choose an artist',
          content: renderArtistSelection(),
          requiresValidation: true,
          isValidated: !!selectedArtist
        },
        {
          label: 'Select Items',
          description: 'Choose items to merge',
          content: renderItemSelection(),
          requiresValidation: true,
          isValidated: selectedItems.length >= 2
        },
        {
          label: 'Confirm',
          description: 'Review and submit',
          content: renderConfirmation()
        }
      ];

  return (
    <Wizard
      steps={wizardSteps}
      finishStep={renderFinishStep()}
      onSubmit={handleWizardSubmit}
      showStepper={true}
    />
  );
}
